import React from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import { THEME_PRESETS, ThemePreset } from '../../constants/themePresets';
import { brandColors } from '../../theme/appTheme';

interface ThemePresetPickerProps {
  active: Pick<ThemePreset, 'sidebarColor' | 'tableColor' | 'formHeaderColor' | 'primaryColor'>;
  disabled?: boolean;
  onSelect: (preset: ThemePreset) => void;
}

function isActive(
  active: ThemePresetPickerProps['active'],
  preset: ThemePreset,
): boolean {
  return active.primaryColor === preset.primaryColor
    && active.sidebarColor === preset.sidebarColor
    && active.tableColor === preset.tableColor;
}

/** Compact 5-color swatch picker — replaces old Glass/Dark/Google cards. */
export const ThemePresetPicker: React.FC<ThemePresetPickerProps> = ({
  active, disabled, onSelect,
}) => (
  <Box>
    <Typography fontWeight={800} fontSize={14} sx={{ mb: 0.5 }}>
      Color presets
    </Typography>
    <Typography fontSize={12} color="text.secondary" sx={{ mb: 1.75 }}>
      Pick one of 5 brand colors. Preview updates instantly — Save Settings to apply for everyone.
    </Typography>
    <Box sx={{
      display: 'flex',
      flexWrap: 'wrap',
      alignItems: 'flex-start',
      gap: 2,
    }}>
      {THEME_PRESETS.map((preset) => {
        const selected = isActive(active, preset);
        return (
          <Tooltip key={preset.name} title={`${preset.name} — ${preset.description}`} arrow>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 0.75,
                width: 56,
              }}
            >
              <Box
                component="button"
                type="button"
                disabled={disabled}
                aria-label={preset.name}
                aria-pressed={selected}
                onClick={() => !disabled && onSelect(preset)}
                sx={{
                  width: 44,
                  height: 44,
                  p: 0,
                  borderRadius: '50%',
                  border: selected ? `3px solid ${preset.swatch}` : `2px solid ${brandColors.border}`,
                  bgcolor: preset.swatch,
                  cursor: disabled ? 'default' : 'pointer',
                  opacity: disabled ? 0.55 : 1,
                  outline: selected ? `3px solid ${preset.swatch}33` : 'none',
                  outlineOffset: 2,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: selected ? `0 4px 14px ${preset.swatch}55` : 'none',
                  transition: 'transform 0.15s, box-shadow 0.15s, border-color 0.15s',
                  '&:hover': disabled ? {} : {
                    transform: 'scale(1.06)',
                    boxShadow: `0 6px 16px ${preset.swatch}44`,
                  },
                }}
              >
                {selected && <CheckIcon sx={{ fontSize: 20, color: '#fff' }} />}
              </Box>
              <Typography
                fontSize={11}
                fontWeight={selected ? 800 : 600}
                color={selected ? 'text.primary' : 'text.secondary'}
                textAlign="center"
              >
                {preset.name}
              </Typography>
            </Box>
          </Tooltip>
        );
      })}
    </Box>
  </Box>
);
